import { sceneToSvg, svgToPng } from './export.ts'
import { addLocalAsset, assetBlob, isLocalAsset } from './localAssets.ts'
import { ensureProject, project } from './project.ts'
import { pasteNodes, scene, selectedNodes } from './scene/store.ts'
import type { SceneNode } from './scene/types.ts'

/**
 * A web custom format: only pages can read it, so other apps see just the PNG
 * written beside it and paste an image.
 */
const NODES_MIME = 'web application/x-lxcanvas+json'
const PASTE_OFFSET = 16

interface Payload {
  version: 1
  projectId: string | null
  nodes: SceneNode[]
  /** Image data by asset id, so a paste into another project can re-upload it. */
  assets: Record<string, string>
}

async function blobToDataUri(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })
}

async function buildPayload(nodes: SceneNode[]): Promise<Payload> {
  const assets: Record<string, string> = {}
  for (const n of nodes) {
    if (n.type !== 'image' || assets[n.assetId]) continue
    try {
      assets[n.assetId] = await blobToDataUri(await assetBlob(n.assetId))
    } catch (err) {
      console.warn('Copy without image data', n.assetId, err)
    }
  }
  return { version: 1, projectId: project()?.id ?? null, nodes: structuredClone(nodes), assets }
}

async function renderPng(): Promise<Blob> {
  const result = await sceneToSvg({ selectionOnly: true, background: false, padding: 0 })
  if (!result) throw new Error('Nothing to copy')
  return await svgToPng(result.svg, result.width, result.height, 1)
}

export function copySelection(): boolean {
  // Paint order, so a paste stacks the copies the way the originals were.
  const nodes = selectedNodes()
    .slice()
    .sort((a, b) => scene.order.indexOf(a.id) - scene.order.indexOf(b.id))
  if (nodes.length === 0 || !navigator.clipboard?.write) return false

  // Promises rather than blobs: the write has to start inside the key event to
  // keep its user activation, but rendering finishes after it.
  const items: Record<string, Promise<Blob>> = { 'image/png': renderPng() }
  if (ClipboardItem.supports?.(NODES_MIME)) {
    items[NODES_MIME] = buildPayload(nodes).then(
      (p) => new Blob([JSON.stringify(p)], { type: NODES_MIME }),
    )
  }
  navigator.clipboard
    .write([new ClipboardItem(items)])
    .catch((err) => console.error('Copy failed', err))
  return true
}

/** Nodes copied from LxCanvas, if that is what the clipboard holds. */
export async function readCopiedNodes(): Promise<Payload | null> {
  try {
    for (const item of await navigator.clipboard.read()) {
      if (!item.types.includes(NODES_MIME)) continue
      const payload = JSON.parse(await (await item.getType(NODES_MIME)).text()) as Payload
      return payload.version === 1 ? payload : null
    }
  } catch (err) {
    // Unsupported, or clipboard-read permission refused: fall back to the image.
    console.warn('Could not read copied layers', err)
  }
  return null
}

let lastPaste: { key: string; count: number } | null = null

export async function pasteCopiedNodes(payload: Payload): Promise<void> {
  const sameProject = payload.projectId !== null && payload.projectId === project()?.id
  const ids = new Map<string, string>()
  for (const n of payload.nodes) {
    if (n.type !== 'image' || ids.has(n.assetId)) continue
    // Stored assets resolve in their own project; anything else is re-added.
    if ((sameProject && !n.assetId.startsWith('local:')) || isLocalAsset(n.assetId)) continue
    const uri = payload.assets[n.assetId]
    if (!uri) continue
    const blob = await (await fetch(uri)).blob()
    ids.set(n.assetId, addLocalAsset(new File([blob], 'pasted', { type: blob.type }), ensureProject))
  }
  const nodes = payload.nodes.map((n) =>
    n.type === 'image' && ids.has(n.assetId) ? { ...n, assetId: ids.get(n.assetId)! } : n,
  )

  // Repeated pastes of one copy cascade instead of stacking exactly.
  const key = payload.nodes.map((n) => n.id).join()
  lastPaste = lastPaste?.key === key ? { key, count: lastPaste.count + 1 } : { key, count: 1 }
  pasteNodes(nodes, PASTE_OFFSET * lastPaste.count, 'Paste')
}
